import SpineWrapper from '@Core/Animation/SpineWrapper';
import { IModifier } from '@Core/Events/EventModifiers/Modifier';
import { SpecialSymbolModifier } from '@Core/Events/EventModifiers/SpecialSymbolModifier';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISymbolConfig } from '@GFTypes/GameStoreTypes';
import { TSymbolSpineAsset } from '@GFTypes/SymbolAsset';
import { Container } from 'pixi.js';
import { GameScene } from '..';

export class SymbolSpinePool {
    private readonly scene: GameScene;
    private readonly pool: Map<string, SpineWrapper[]>;
    private readonly poolContainer: Container;
    private configs: ISymbolConfig[];
    constructor(
        scene: GameScene,
        private readonly symbolConfigs: ISymbolConfig[],
        multiSymbolAsset?: TSymbolSpineAsset
    ) {
        this.scene = scene;
        this.pool = new Map();
        this.poolContainer = new Container({ parent: this.scene.stage });

        const numberOfReels = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfReels);
        const symbolsPerReelWithTopAndBottom = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel) + 2;
        const isDoubleReelsFeature = !!this.scene.gameStore.get(EGameStoreKeys.Features).multiSymbol;
        const numberOfSpinningSpines = (isDoubleReelsFeature ? numberOfReels * 2 : numberOfReels) * symbolsPerReelWithTopAndBottom;
        this.scene.game.gameStore.set(EGameStoreKeys.NumberOfSpinningSpines, numberOfSpinningSpines);
        symbolConfigs.forEach((config) => this.populatePool(config, numberOfSpinningSpines));
        if (multiSymbolAsset) this.populatePool(multiSymbolAsset, numberOfSpinningSpines);
    }

    public populatePool(config: ISymbolConfig | TSymbolSpineAsset, amount: number, additionalModifiers: IModifier[] = []): void {
        const dataKey = this.isSymbolConfig(config) ? config.asset.dataKey : config.dataKey;

        if (this.pool.has(dataKey)) return;

        const baseModifiers = this.isSymbolConfig(config) ? [new SpecialSymbolModifier(this.scene.game.gameStore, config.id)] : [];
        const modifiers = [...baseModifiers, ...additionalModifiers];

        const spines = [];
        for (let i = 0; i < amount; i++) {
            const spine = new SpineWrapper(this.scene, { x: 0, y: 0 }, { dataKey, manuallyScaleOnResize: true }, true, true, modifiers);
            spine.eventManager.playEventAnimation(ESymbolAnimations.DEFAULT);
            spine.forceUpdate();
            spine.spine.visible = false;
            spine.addToContainer(this.poolContainer);
            spines.push(spine);
        }

        this.pool.set(dataKey, spines);
    }

    private isSymbolConfig(config: ISymbolConfig | TSymbolSpineAsset): config is ISymbolConfig {
        return 'asset' in config;
    }

    public getSpine(key: string, active: boolean = true, visible: boolean = true): SpineWrapper {
        if (this.pool.has(key)) {
            const spine = this.pool.get(key)!.pop();

            if (!spine) throw new Error('Exceeded max amount of declared spines in spine pool for key: ' + key);

            spine.spine.visible = true;

            return spine;
        } else {
            throw new Error('Spine pool does not contain spines with passed key: ' + key);
        }
    }

    public releaseSpine(key: string, spine: SpineWrapper): void {
        if (this.pool.has(key)) {
            const spines = this.pool.get(key)!;
            spine.spine.skeleton.setupPose();
            spine.spine.state.clearTracks();
            spine.spine.visible = false;
            spines.push(spine);
            this.pool.set(key, spines);
        } else {
            throw new Error('Spine pool does not contain spines with passed key: ' + key);
        }
    }
}
